/**
 * Billing Data Export Utilities
 *
 * Provides functions to export agency billing data in various formats (CSV, JSON)
 */

import { ClientBilling, AgencyBillingSummary, Invoice, UsageChartData } from "@/types/billing";

/** Format for export file naming */
const formatExportDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0].replace(/-/g, '');
};

/** Format currency without symbol for CSV */
const formatCurrencyRaw = (amount: number): string => {
  return amount.toFixed(2);
};

/** Escape CSV values to handle commas and quotes */
const escapeCSV = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/** Generate CSV content from array of objects */
const generateCSV = (headers: string[], rows: (string | number)[][]): string => {
  const headerRow = headers.map(escapeCSV).join(',');
  const dataRows = rows.map(row => row.map(escapeCSV).join(','));
  return [headerRow, ...dataRows].join('\n');
};

/** Trigger download of a file */
const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export interface ExportOptions {
  periodLabel?: string;
  includeUsageDetails?: boolean;
  format?: 'csv' | 'json';
}

/**
 * Export client billing summary to CSV
 */
export function exportClientBillingCSV(
  clients: ClientBilling[],
  summary: AgencyBillingSummary | null | undefined,
  options: ExportOptions = {}
): void {
  const { periodLabel = 'current', includeUsageDetails = false } = options;
  const dateStr = formatExportDate();

  // Basic billing summary
  const headers = [
    'Client Name',
    'Status',
    'Base Cost ($)',
    'Markup %',
    'Markup Amount ($)',
    'Total Billed ($)',
    'Unbilled Amount ($)',
    'Last Invoice Date',
  ];

  // Add usage columns if requested
  if (includeUsageDetails) {
    headers.push(
      'Voice Minutes',
      'Voice Cost ($)',
      'SMS Count',
      'SMS Cost ($)',
      'Transcription Chars',
      'Transcription Cost ($)',
      'AI Requests',
      'AI Cost ($)'
    );
  }

  const rows = clients.map(client => {
    const baseRow: (string | number)[] = [
      client.tenant_name,
      client.status,
      formatCurrencyRaw(client.base_cost),
      client.markup_percentage,
      formatCurrencyRaw(client.markup_amount),
      formatCurrencyRaw(client.total_billed),
      formatCurrencyRaw(client.unbilled_amount),
      client.last_invoice_date || 'N/A',
    ];

    if (includeUsageDetails && client.usage_summary) {
      const usage = client.usage_summary;
      baseRow.push(
        usage.voice_minutes,
        formatCurrencyRaw(usage.voice_cost),
        usage.sms_count,
        formatCurrencyRaw(usage.sms_cost),
        usage.transcription_chars,
        formatCurrencyRaw(usage.transcription_cost),
        usage.ai_requests,
        formatCurrencyRaw(usage.ai_cost)
      );
    }

    return baseRow;
  });

  // Add summary row if available
  if (summary) {
    const summaryRow: (string | number)[] = [
      'TOTAL',
      '',
      formatCurrencyRaw(summary.total_base_cost),
      '',
      formatCurrencyRaw(summary.total_markup),
      formatCurrencyRaw(summary.total_billed),
      formatCurrencyRaw(summary.unbilled_amount),
      '',
    ];

    if (includeUsageDetails) {
      // Fill empty cells for usage columns in summary
      summaryRow.push('', '', '', '', '', '', '', '');
    }

    rows.push(summaryRow);
  }

  const csv = generateCSV(headers, rows);
  downloadFile(csv, `billing-summary-${periodLabel}-${dateStr}.csv`, 'text/csv;charset=utf-8');
}

/**
 * Export invoice history to CSV
 */
export function exportInvoicesCSV(
  invoices: Invoice[],
  clientName?: string
): void {
  const dateStr = formatExportDate();

  const headers = [
    'Invoice Number',
    'Client Name',
    'Status',
    'Period Start',
    'Period End',
    'Base Amount ($)',
    'Markup Amount ($)',
    'Total Amount ($)',
    'Due Date',
    'Paid Date',
    'Created At',
  ];

  const rows = invoices.map(invoice => [
    invoice.invoice_number,
    invoice.tenant_name || clientName || '',
    invoice.status,
    invoice.period_start,
    invoice.period_end,
    formatCurrencyRaw(invoice.base_amount),
    formatCurrencyRaw(invoice.markup_amount),
    formatCurrencyRaw(invoice.amount),
    invoice.due_date,
    invoice.paid_date || 'N/A',
    invoice.created_at,
  ]);

  const csv = generateCSV(headers, rows);
  const prefix = clientName ? `${clientName.replace(/\s+/g, '-').toLowerCase()}-` : '';
  downloadFile(csv, `${prefix}invoices-${dateStr}.csv`, 'text/csv;charset=utf-8');
}

/**
 * Export usage chart data to CSV
 */
export function exportUsageDataCSV(
  usageData: UsageChartData[],
  options: ExportOptions = {}
): void {
  const { periodLabel = 'current' } = options;
  const dateStr = formatExportDate();

  const headers = [
    'Date',
    'Voice Cost ($)',
    'SMS Cost ($)',
    'Transcription Cost ($)',
    'AI Cost ($)',
    'Total Cost ($)',
  ];

  const rows = usageData.map(data => [
    data.date,
    formatCurrencyRaw(data.voice),
    formatCurrencyRaw(data.sms),
    formatCurrencyRaw(data.transcription),
    formatCurrencyRaw(data.ai),
    formatCurrencyRaw(data.total),
  ]);

  const csv = generateCSV(headers, rows);
  downloadFile(csv, `usage-data-${periodLabel}-${dateStr}.csv`, 'text/csv;charset=utf-8');
}

/**
 * Export complete billing report as JSON
 */
export function exportBillingReportJSON(
  clients: ClientBilling[],
  summary: AgencyBillingSummary | null | undefined,
  usageData: UsageChartData[],
  options: ExportOptions = {}
): void {
  const { periodLabel = 'current' } = options;
  const dateStr = formatExportDate();

  const report = {
    exported_at: new Date().toISOString(),
    period: periodLabel,
    summary: summary || null,
    clients,
    usage_timeline: usageData,
  };

  const json = JSON.stringify(report, null, 2);
  downloadFile(json, `billing-report-${periodLabel}-${dateStr}.json`, 'application/json');
}
